import { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Image as ImageIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import { Conversation } from '../../types/chat';
import { useChatStore } from '../../stores/chatStore';
import { UserAvatar } from '../Common/UserAvatar';

/**
 * Format last message for better UX
 */
const formatLastMessage = (content: string) => {
    const imageRegex = /(https?:\/\/[^\s]+?\.(?:jpg|jpeg|png|webp|gif|svg)|https?:\/\/blossom[^\s]+)/gi;
    if (imageRegex.test(content)) {
        const textOnly = content.replace(imageRegex, '').trim();
        return (
            <span className="flex items-center gap-1">
                <ImageIcon className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{textOnly || 'Görsel paylaştı'}</span>
            </span>
        );
    }
    return content;
};

interface ConversationListProps {
    conversations: Conversation[];
}

/**
 * Conversation list - Sol sidebar
 * Shows avatar, name, last message, timestamp
 */
export function ConversationList({ conversations }: ConversationListProps) {
    const { activeConversation, setActiveConversation } = useChatStore();
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = 0;
        }
    }, [conversations.length > 0]);

    if (conversations.length === 0) {
        return (
            <div className="flex-1 flex items-center justify-center p-6 text-center">
                <div className="space-y-2">
                    <p className="font-poppins font-semibold text-gray-700 dark:text-gray-300">
                        Henüz mesaj yok
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Bir kullanıcıya mesaj göndererek başlayın
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div ref={listRef} className="flex-1 overflow-y-auto">
            {conversations.map((conv) => (
                <motion.button
                    key={conv.pubkey}
                    onClick={() => setActiveConversation(conv.pubkey)}
                    whileHover={{ backgroundColor: 'rgba(0, 112, 161, 0.05)' }}
                    className={`w-full p-3 flex items-start gap-3 border-b border-surface-100 dark:border-surface-800 transition-colors ${activeConversation === conv.pubkey
                        ? 'bg-mavi-50 dark:bg-mavi-900/20 border-l-4 border-l-mavi-500'
                        : 'border-l-4 border-l-transparent'
                        }`}
                >
                    {/* Avatar */}
                    <div className="relative flex-shrink-0">
                        <UserAvatar
                            src={conv.avatar}
                            name={conv.name}
                            size="lg"
                            isExternal={false}
                        />

                        {/* Unread badge */}
                        {conv.unread > 0 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-kirmizi-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white dark:border-surface-900">
                                {conv.unread > 9 ? '9+' : conv.unread}
                            </div>
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 text-left overflow-hidden">
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <p className="font-poppins font-semibold text-sm text-gray-900 dark:text-white truncate">
                                {conv.name}
                            </p>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">
                                {formatDistanceToNow(conv.timestamp, {
                                    addSuffix: false,
                                    locale: tr,
                                })}
                            </span>
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 font-merriweather truncate">
                            {formatLastMessage(conv.lastMessage)}
                        </div>
                    </div>
                </motion.button>
            ))}
        </div>
    );
}
